import { useState, useEffect } from "react";
import { useCustomer } from "@/contexts/CustomerContext";
import {
  clearCart,
  selectCartItemCount,
  validateCart,
} from "@/store/features/cartSlice";
import {
  faShoppingCart,
  faUser,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import CartPage from "../cart/CartPage";
import logo from "@/assets/img/logo.png";
import { motion } from "motion/react";
import { Dock, DockIcon } from "@/components/ui/dock";

interface HeaderProps {
  isSelectedBranchOpen: boolean;
}

export default function Header({ isSelectedBranchOpen }: HeaderProps) {
  const itemCount = useSelector(selectCartItemCount);
  const validCart = useSelector(validateCart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasScrolled, setHasScrolled] = useState(false);

  const { customer } = useCustomer();
  const merchantSlug = useMerchantSlug();

  // Update paths using merchantSlug
  const cartPath = `${merchantSlug}/cart`;
  const customerProfilePath = `${merchantSlug}/customer-profile`;
  const customerLoginPath = `${merchantSlug}/customer-login`;
  const homePath = "/";

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Disable/enable scroll on body when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isCartOpen]);

  // Handle cart click with desktop/mobile distinction
  const handleCartClick = () => {
    if (!isMobile) {
      setIsCartOpen(true);
    } else {
      navigate(cartPath);
    }
  };

  const handleCartClose = () => {
    setIsCartOpen(false);
  };

  // Navigate to profile or login page
  const handleProfileClick = () => {
    navigate(customer?.name ? customerProfilePath : customerLoginPath);
  };

  // Navigate to home
  const handleHomeClick = () => {
    navigate(homePath);
  };

  return (
    <>
      <header
        className={`sticky z-40 flex justify-center transition-all duration-300 ${
          isMobile
            ? hasScrolled
              ? "top-1 mx-0 px-0 w-full"
              : "top-1 mx-0 px-0 w-full"
            : hasScrolled
              ? "top-6 mx-4"
              : "top-4 mx-0"
        }`}
      >
        <motion.div
          initial={{ width: isMobile ? "100%" : "70rem" }}
          animate={{
            width: isMobile ? "100%" : hasScrolled ? "1000px" : "70rem",
          }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-full md:w-auto"
        >
          <div
            className={`mx-auto w-full max-w-7xl rounded-2xl transition-all duration-300 xl:px-0 ${
              hasScrolled
                ? isMobile
                  ? "px-0 border border-gray-200 backdrop-blur-lg bg-white/75"
                  : "px-6 border border-gray-200 backdrop-blur-lg bg-white/75"
                : isMobile
                  ? "shadow-none px-0"
                  : "shadow-none px-7 md:px-7"
            }`}
          >
            <div
              className={`flex h-[56px] md:h-[56px] items-center justify-between ${isMobile ? "px-1" : "p-2 md:p-4"}`}
            >
              {/* Logo */}
              <div
                className="flex items-center gap-2 md:gap-3 cursor-pointer pl-5 md:pl-2"
                onClick={handleHomeClick}
              >
                <img src={logo} alt="Redbird Logo" className="h-8 md:h-12" />
              </div>

              {/* Actions */}
              <div className="flex flex-row items-center gap-6 md:gap-3 shrink-0 pr-5 md:pr-2">
                {/* Sign In/Profile Button */}
                <div className="hidden md:flex items-center">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center space-x-2 text-base text-gray-700 hover:text-redCrimson"
                  >
                    <FontAwesomeIcon icon={faUser} className="text-lg" />
                    <span>{customer?.name ? customer.name : "Sign In"}</span>
                  </button>
                </div>

                {/* Mobile Sign In Button */}
                <div className="md:hidden">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center justify-center h-11 w-11 rounded-full bg-gray-100 hover:bg-gray-200"
                  >
                    <FontAwesomeIcon
                      icon={faUser}
                      className="text-xl text-gray-700"
                    />
                  </button>
                </div>

                {/* Cart Button */}
                <button
                  onClick={handleCartClick}
                  className="relative flex items-center justify-center h-11 w-11 md:h-11 md:w-11 rounded-full bg-gray-100 hover:bg-gray-200"
                >
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    className="text-xl md:text-xl text-gray-700"
                  />
                  {itemCount > 0 && (
                    <div className="absolute -top-1 -right-1 h-5 w-5 md:h-6 md:w-6 flex items-center justify-center rounded-full bg-redCrimson text-white text-xs md:text-sm">
                      {itemCount}
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cart Overlay */}
        {isCartOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-40"
            onClick={handleCartClose}
          />
        )}

        {/* Slide-in Cart Component */}
        <div
          className={`fixed top-0 right-0 w-full md:w-1/3 h-full bg-white shadow-lg transition-transform duration-300 transform z-50 ${
            isCartOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <CartPage handleCartClose={handleCartClose} />
        </div>
      </header>

      {/* Mobile Dock Navigation */}
      {isMobile && (
        <div className="fixed bottom-4 left-0 right-0 flex justify-center z-50">
          <Dock className="bg-white/90 border border-gray-200 shadow-lg rounded-full">
            <DockIcon
              className="bg-gray-100 hover:bg-gray-200"
              onClick={handleProfileClick}
              size={60}
              magnification={75}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="text-2xl text-gray-700"
              />
            </DockIcon>
          </Dock>
        </div>
      )}
    </>
  );
}
